// src/components/ProductCard.js
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import AddToCartSelectorDrawer from "./AddToCartSelectorDrawer";

const SIZE_SCALE = [36, 37, 38, 39, 40, 41];

function ProductCard({ product }) {
  const { lang } = useContext(LanguageContext);
  const { toggleWishlist, isInWishlist } = useCart();
  const navigate = useNavigate();

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  
  const isRTL = lang === "AR";

const isMobile =
  typeof window !== "undefined" && window.innerWidth < 768;

  const inWishlist = isInWishlist(product.id);

  const mainImage =
  product.images?.[0] ||
  product.image ||
  "/images/placeholder-product.jpg";


  const displayName = lang === "EN" ? product.nameEN : product.nameAR;
  const hasDiscount = Number(product.discount) > 0;
  
const availableSizes = new Set(
  (product.sizes || []).flatMap((s) => {
    if (!s.size) return [];

    // handle combined sizes like "36/37"
    if (typeof s.size === "string" && s.size.includes("/")) {
      return s.size
        .split("/")
        .map((v) => Number(v.trim()))
        .filter((v) => !Number.isNaN(v));
    }

    const n = Number(s.size);
    return Number.isNaN(n) ? [] : [n];
  })
);


  const hasOneSize = (product.sizes || []).some(
  (s) => String(s.size).toUpperCase() === "ONE"
     );


  const finalPrice = hasDiscount
    ? Math.round(product.price * (1 - product.discount / 100))
    : product.price;

      const totalStock = Number(product.totalStock) || 0;

  const stockInfo = (() => {
    if (totalStock <= 0) {
      return {
        text: lang === "EN" ? "OUT OF STOCK" : "غير متوفر",
        cls: "text-red-600",
      };
    }
    if (totalStock <= 3) {
      return {
        text:
          lang === "EN"
            ? `ONLY ${totalStock} LEFT`
            : `باقي ${totalStock} فقط`,
        cls: "text-red-400",
      };
    }
    return {
      text: lang === "EN" ? "IN STOCK" : "متوفر",
      cls: "text-green-600",
    };
  })();



  // Open product details
  const handleOpenDetails = () => navigate(`/product/${product.slug}`);

  // Open selector drawer
  const openSelector = (e) => {
    e.stopPropagation();
    setSelectorOpen(true);
  };

  // Wishlist toggle (backend or local handled inside CartContext)
  const handleToggleWishlist = (e) => {
    e.stopPropagation();
    toggleWishlist({
      id: product.id,
      nameEN: product.nameEN,
      nameAR: product.nameAR,
      price: finalPrice,
      image: mainImage,
    });
  };



  return (
    <>
      {/* CARD */}
      <div
  role="button"
  tabIndex={0}
  onClick={handleOpenDetails}
  className="
    group w-full text-left bg-white
    border border-[#E6D8D2] rounded-lg
    overflow-hidden
    [content-visibility:auto]
    [contain-intrinsic-size:400px]
  "
>
        {/* IMAGE */}
        <div className="relative overflow-hidden">
          <img
  src={mainImage}
  alt={displayName}
  loading="lazy"
  decoding="async"
  fetchpriority="low"
  width="400"
  height="400"
  className={`
    w-full aspect-square object-cover
    ${!isMobile ? "transition-transform duration-300 group-hover:scale-110" : ""}
  `}
/>
          {/* DISCOUNT TAG */}
          {hasDiscount && (
            <span className="absolute top-2 left-2 bg-black text-white text-[11px] px-2 py-[3px] rounded-full">
              -{product.discount}%
            </span>
          )}

          {/* WISHLIST HEART */}
          <button
            onClick={handleToggleWishlist}
            className="
              absolute top-2 right-2 w-8 h-8 
              rounded-full bg-white/80 
              flex items-center justify-center
              backdrop-blur-sm shadow-sm
              hover:bg-white transition
            "
          >
            <span
              className={`text-lg ${
                inWishlist ? "text-red-500" : "text-[#7A7674]"
              }`}
            >
              {inWishlist ? "♥" : "♡"}
            </span>
          </button>
        </div>

        {/* DETAILS */}
        <div className="px-3 pt-3 pb-3">
          <p className="text-[13px] md:text-sm font-semibold text-[#2E3135] line-clamp-2">
  {displayName}
</p>

{product.public_id && (
  <p className="text-[11px] text-[#9F9087] mt-[2px]">
    {product.public_id}
  </p>
)}

{/* ✅ NEW */}
<p className={`text-[11px] mt-[2px] font-semibold ${stockInfo.cls}`}>
  {stockInfo.text}
</p>


          <div className="mt-2 flex items-center gap-2">
            <span className="text-[13px] md:text-sm font-bold text-black">
              ${finalPrice}
            </span>
            {hasDiscount && (
              <span className="text-[11px] line-through text-[#9F9087]">
                ${product.price}
              </span>
            )}
          </div>

          {/* COLORS */}
          {product.colors?.length > 0 && (
            <div className="flex items-center gap-1 mt-2">
              {product.colors.slice(0, 4).map((c, idx) => (
                <span
                  key={idx}
                  className="w-3 h-3 rounded-full border border-[#D3C5BE]"
                  style={{ backgroundColor: c.code }}
                ></span>
              ))}
            </div>
          )}

              {/* SIZE AVAILABILITY */}
{hasOneSize ? (
  <div className="mt-2 inline-flex px-3 py-1 text-[11px] font-semibold border border-[#2E3135] rounded-md text-[#2E3135]">
    {lang === "EN" ? "ONE SIZE" : "مقاس واحد"}
  </div>
) : (
  <div className="flex gap-1.5 mt-2 flex-wrap">
  {SIZE_SCALE.map((size) => {
    const isAvailable = availableSizes.has(size);

    return (
      <div
        key={size}
        className={`
          relative
          w-5 h-5
          text-[11px] font-medium
          flex items-center justify-center
          rounded-full
          transition-all
          ${
            isAvailable
              ? "bg-[#F7F3F1] text-[#2E3135] border border-[#2E3135]"
              : "bg-[#FAF8F7] text-[#B3A9A3] border border-[#E6D8D2]"
          }
        `}
      >
        {size}

        {!isAvailable && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-[120%] h-[1px] bg-[#B3A9A3] rotate-[-45deg]" />
          </span>
        )}
      </div>
    );
  })}
</div>
)}



          {/* ADD TO CART */}
          <button
            onClick={openSelector}
            className="
              mt-3 w-full py-2 
              bg-black text-white 
              text-[12px] md:text-sm 
              rounded-md font-semibold 
              tracking-wide hover:bg-[#222]
            "
          >
            {lang === "EN" ? "ADD TO CART" : "أضيفي إلى السلة"}
          </button>
        </div>
      </div>

      {/* SELECTOR DRAWER */}
      {selectorOpen && (
  <AddToCartSelectorDrawer
    isOpen={selectorOpen}
    onClose={() => {
      setSelectorOpen(false);
      setSelectedSize(null);
      setSelectedColor(null);
    }}
    product={product}
    selectedSize={selectedSize}
    selectedColor={selectedColor}
    setSelectedSize={setSelectedSize}
    setSelectedColor={setSelectedColor}
  />
)}
    </>
  );
}

export default ProductCard;
